import { ChangeDetectionStrategy, Component, Output, EventEmitter, Input } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatRippleModule } from '@angular/material/core'
import { MatSidenavModule } from '@angular/material/sidenav'
import { TranslateModule } from '@ngx-translate/core'
import { GbDashboardSharedUiModule, IconLibrary } from 'libs/gb-dashboard/shared/ui/src'
import { PresenterElementTypeEnum, ProjectorsFacade } from 'libs/gb-dashboard/projector/data-access/src'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { CommonModule } from '@angular/common'
import { GbDashboardSharedFeatureModule } from '@galabid/gb-dashboard/shared/feature'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { combineLatest, map } from 'rxjs'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { MatIconModule } from '@angular/material/icon'

export interface PresenterElement {
  id: PresenterElementTypeEnum
  label: string
  icon: string
  library: IconLibrary
  onClick?: () => void
  description?: string
  isDivider?: boolean
  staffOnly?: boolean
  enableAdd?: boolean
}

@Component({
  selector: 'gb-presenter-elements-list',
  imports: [
    GbDashboardSharedFeatureModule,
    CommonModule,
    MatSidenavModule,
    NgScrollbarModule,
    GbDashboardSharedUiModule,
    MatButtonModule,
    MatRippleModule,
    TranslateModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './gb-presenter-elements-list.component.html',
  styleUrl: './gb-presenter-elements-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterElementsListComponent {
  @Output() addElement = new EventEmitter<PresenterElementTypeEnum>()
  @Output() toggleCustomCss = new EventEmitter<void>()
  @Output() openTemplatesDialog = new EventEmitter<void>()

  @Input() templateMode = false
  @Input() addElementLoaded: Partial<Record<PresenterElementTypeEnum, boolean>> | null = null

  IconLibrary = IconLibrary

  get elementsListItems() {
    if (this.templateMode) {
      return this.elements.filter((element) => element.id !== PresenterElementTypeEnum.TEMPLATES)
    } else {
      return this.elements
    }
  }

  elements: PresenterElement[] = [
    {
      id: PresenterElementTypeEnum.TEMPLATES,
      label: 'PRESENTER.ELEMENTS.TEMPLATES',
      icon: 'bento',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.TEMPLATES_DESCRIPTION',
      onClick: () => this.openTemplatesDialog.emit(),
    },
    {
      id: PresenterElementTypeEnum.DIVIDER,
      label: 'PRESENTER.ADD_ELEMENTS',
      icon: '',
      library: IconLibrary.material,
      isDivider: true,
    },
    {
      id: PresenterElementTypeEnum.TEXT,
      label: 'PRESENTER.ELEMENTS.TEXT',
      icon: 'text_fields',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.TEXT_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.SMART_TEXT,
      label: 'PRESENTER.ELEMENTS.SMART_TEXT',
      icon: 'code-slash',
      library: IconLibrary.ionic,
      description: 'PRESENTER.ELEMENTS.SMART_TEXT_DESCRIPTION',
      enableAdd: true,
    },

    {
      id: PresenterElementTypeEnum.ITEM_TABLE,
      label: 'PRESENTER.ELEMENTS.ITEM_TABLE',
      icon: 'table_chart',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.ITEM_TABLE_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.TOTAL_RAISED,
      label: 'PRESENTER.ELEMENTS.TOTAL_RAISED',
      icon: 'attach_money',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.TOTAL_RAISED_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.NOTIFICATION,
      label: 'PRESENTER.ELEMENTS.NOTIFICATION',
      icon: 'notifications',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.NOTIFICATION_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.THERMOMETER,
      label: 'PRESENTER.ELEMENTS.THERMOMETER',
      icon: 'thermostat',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.THERMOMETER_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.COUNTDOWN_TIMER,
      label: 'PRESENTER.ELEMENTS.COUNTDOWN_TIMER',
      icon: 'timer',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.COUNTDOWN_TIMER_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.HONOUR_ROLL,
      label: 'PRESENTER.ELEMENTS.HONOUR_ROLL',
      icon: 'stars',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.HONOUR_ROLL_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.QR_CODE,
      label: 'PRESENTER.ELEMENTS.QR_CODE',
      icon: 'qr_code',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.QR_CODE_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.TEAM_SCORING,
      label: 'PRESENTER.ELEMENTS.TEAM_SCORING',
      icon: 'leaderboard',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.TEAM_SCORING_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.DIVIDER_MEDIA,
      label: 'PRESENTER.MEDIA',
      icon: '',
      library: IconLibrary.material,
      isDivider: true,
    },
    {
      id: PresenterElementTypeEnum.VIDEO,
      label: 'PRESENTER.ELEMENTS.VIDEO',
      icon: 'logo-youtube',
      library: IconLibrary.ionic,
      description: 'PRESENTER.ELEMENTS.VIDEO_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.IMAGE,
      label: 'PRESENTER.ELEMENTS.IMAGE',
      icon: 'image',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.IMAGE_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.DIVIDER_RAFFLE,
      label: 'PRESENTER.RAFFLE',
      icon: '',
      library: IconLibrary.material,
      isDivider: true,
    },
    {
      id: PresenterElementTypeEnum.RAFFLE_DRAW,
      label: 'PRESENTER.ELEMENTS.RAFFLE_DRAW',
      icon: 'casino',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.RAFFLE_DRAW_DESCRIPTION',
      enableAdd: true,
    },
    {
      id: PresenterElementTypeEnum.RAFFLE_WINNERS,
      label: 'PRESENTER.ELEMENTS.RAFFLE_WINNERS',
      icon: 'emoji_events',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.RAFFLE_WINNERS_DESCRIPTION',
      enableAdd: true,
    },

    {
      id: PresenterElementTypeEnum.CUSTOM_CSS,
      label: 'PRESENTER.ELEMENTS.CUSTOM_CSS',
      icon: 'code',
      library: IconLibrary.material,
      description: 'PRESENTER.ELEMENTS.CUSTOM_CSS_DESCRIPTION',
      onClick: () => this.toggleCustomCss.emit(),
      staffOnly: true,
    },
  ]

  elements$ = this.selectedAuctionFacade.isRaffleEnabled$.pipe(
    map((isRaffleEnabled) => {
      if (this.templateMode) {
        return this.elements.filter((element) => element.id !== PresenterElementTypeEnum.TEMPLATES)
      }

      if (!isRaffleEnabled) {
        return this.elements.filter((element) => !this.isRaffleElement(element.id))
      }

      return this.elements
    }),
  )

  auctionLoaded$ = combineLatest([this.selectedAuctionFacade.auction$, this.projectorsFacade.loaded$]).pipe(
    map(([auction, loaded]) => !!auction && !!loaded),
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private projectorsFacade: ProjectorsFacade,
  ) {}

  isRaffleElement(id: PresenterElementTypeEnum): boolean {
    return [
      PresenterElementTypeEnum.RAFFLE_DRAW,
      PresenterElementTypeEnum.RAFFLE_WINNERS,
      PresenterElementTypeEnum.DIVIDER_RAFFLE,
    ].includes(id)
  }

  onElementClick(element: PresenterElement, auctionLoaded: boolean | null): void {
    if ((!this.isLoaded(element.id) || !auctionLoaded) && !this.templateMode) {
      return
    }

    if (element.onClick) {
      return element.onClick()
    }

    return this.addElement.emit(element.id)
  }

  onElementDragStart(event: DragEvent, element: PresenterElement): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData('application/json', JSON.stringify(element))
      event.dataTransfer.effectAllowed = 'copy'
    }
  }

  isLoaded(id: PresenterElementTypeEnum) {
    return this.addElementLoaded?.[id] ?? true
  }
}
